import React, { useEffect, useState, useRef } from 'react';
import { Map as OlMap } from 'ol';
import { getPointResolution } from 'ol/proj';
import styles from './ScaleBar.module.css';

interface ScaleBarProps {
    map: OlMap | undefined;
}

const ScaleBar: React.FC<ScaleBarProps> = ({ map }) => {
    const [scaleText, setScaleText] = useState<string>('');
    const [scaleWidth, setScaleWidth] = useState<number>(100);
    const [clusterDistance, setClusterDistance] = useState<string>('');

    const updateScale = () => {
        if (!map) return;

        const view = map.getView();
        const projection = view.getProjection();
        const resolution = view.getResolution();
        const center = view.getCenter();

        if (!resolution || !center) return;

        // 실제 해상도 계산 (위도에 따른 보정)
        const pointResolution = getPointResolution(
            projection,
            resolution,
            center
        );

        // 최대 스케일 바 너비 제한 (픽셀)
        const maxScaleBarWidth = 80;

        // 최대 너비에 해당하는 실제 거리 계산 (미터)
        const maxRealDistance = maxScaleBarWidth * pointResolution;

        // 적절한 단위로 표시
        let displayDistance: number;
        let unit: string;

        if (maxRealDistance >= 1000) {
            displayDistance = Math.round(maxRealDistance / 1000);
            unit = 'km';
        } else {
            displayDistance = Math.round(maxRealDistance);
            unit = 'm';
        }

        // 깔끔한 숫자로 조정 (현재 거리보다 작거나 같은 값 중 가장 큰 값)
        const niceNumbers = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];
        const targetDistance = niceNumbers.reverse().find(num => num <= displayDistance) || 1;
        niceNumbers.reverse(); // 원래 순서로 복원

        // 조정된 거리에 맞는 픽셀 너비 계산
        const adjustedWidth = (targetDistance * (unit === 'km' ? 1000 : 1)) / pointResolution;

        setScaleText(`${targetDistance} ${unit}`);
        setScaleWidth(Math.min(Math.round(adjustedWidth), maxScaleBarWidth));

        // 클러스터링 거리 계산
        const zoom = view.getZoom()?? 0;
        let clusterPixels;
        if (zoom <= 5) {
            clusterPixels = 200;
        }
        else if (zoom <= 11) {
            clusterPixels = 150;
        }
        else {
            clusterPixels = 100;
        }

        const clusterRealDistance = clusterPixels * pointResolution;

        if (clusterRealDistance >= 1000) {
            setClusterDistance(`${(clusterRealDistance / 1000).toFixed(2)}km`);
        } else {
            setClusterDistance(`${clusterRealDistance.toFixed(0)}m`);
        }
    };

    useEffect(() => {
        if (!map) return;

        // 초기 스케일 업데이트
        updateScale();

        // 지도 이동/줌 이벤트 리스너
        const handleViewChange = () => updateScale();

        map.getView().on('change:resolution', handleViewChange);
        map.getView().on('change:center', handleViewChange);

        return () => {
            map.getView().un('change:resolution', handleViewChange);
            map.getView().un('change:center', handleViewChange);
        };
    }, [map]);

    if (!map) return null;

    return (
        <div className={styles.scaleBarContainer}>
            <div className={styles.scaleBar}>
                <div className={styles.scaleText}>{scaleText}</div>
                <div
                    className={styles.scaleLine}
                    style={{ width: `${scaleWidth}px` }}
                />
            </div>
            <div className={styles.clusterInfo}>
                ( 클러스터링 거리: {clusterDistance} )
            </div>
        </div>
    );
};

export default ScaleBar;